import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, ArrowRight } from 'lucide-react';

const AboutPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_coursehub-158/artifacts/mbp7cssq_AHKP4615.JPG"
            alt="Steven Yong"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a]/95 via-[#0a0a0a]/70 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-[#0a0a0a]/30" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="max-w-2xl">
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light text-white mb-8 leading-tight" data-testid="about-hero-title">
              The <span className="font-serif italic text-amber-200">Story</span> of
              <br />
              <span className="text-white">Steven Yong</span>
            </h1>
            
            <div className="space-y-4 text-gray-300 text-lg leading-relaxed mb-10">
              <p>
                Steven started with the same questions most of us face:
              </p>
              <p className="text-white font-medium">
                What's next? When should I act? How do I choose the right path?
              </p>
              <p>
                Through years of study, testing, and teaching, he discovered how to apply classical principles of ancient Chinese Metaphysics to modern life, with a clear framework for evaluating outcomes, choices, and personal growth.
              </p>
              <p>
                Today, that method has helped tens of thousands create better outcomes for themselves and others – with less second-guessing.
              </p>
            </div>

            <Button
              onClick={() => {
                const el = document.getElementById('profile-content');
                el?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="bg-amber-100 hover:bg-amber-200 text-[#0a0a0a] font-medium px-8 py-6 text-base rounded-full"
              data-testid="about-read-story-btn"
            >
              Read His Story
            </Button>
          </div>
        </div>
      </section>

      {/* Profile Content */}
      <section id="profile-content" className="py-24 bg-[#0a0a0a]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Opening */}
          <div className="mb-16 text-center" data-testid="about-block-subtitle">
            <p className="text-xl md:text-2xl text-amber-200 font-light font-serif italic leading-relaxed">
              Translating Ancient Strategy into Modern Advantage
            </p>
          </div>

          {/* The Beginning */}
          <div className="mb-20" data-testid="about-block-opening">
            <div className="space-y-5 text-gray-400 leading-relaxed text-lg">
              <p>
                There was a time when Steven Yong did everything right...
              </p>
              <p>
                and yet, things didn't always go right.
              </p>
              <p className="text-gray-300">
                He worked hard.
                <br />
                He made logical decisions.
                <br />
                He took action when opportunities appeared.
              </p>
              <p>
                But something didn't add up.
              </p>
              <p>
                Sometimes, things flowed effortlessly.
                <br />
                Other times, no matter how much effort he put in... <span className="text-white">nothing worked.</span>
              </p>
            </div>
          </div>

          {/* The Question */}
          <div className="mb-20" data-testid="about-block-question">
            <div className="space-y-5 text-gray-400 leading-relaxed text-lg">
              <p>
                That was when he began asking a different question:
              </p>
              <blockquote className="border-l-2 border-amber-500/60 pl-6 py-2 my-8">
                <p className="text-xl md:text-2xl font-light text-white leading-relaxed font-serif italic">
                  "What if success is not just about what we do... but when we do it?"
                </p>
              </blockquote>
              <p>
                This question led him beyond conventional thinking — into the deeper study of <span className="text-amber-200">ancient strategic systems.</span>
              </p>
            </div>
          </div>

          {/* Zhuge Liang */}
          <div className="mb-20" data-testid="about-block-zhuge">
            <div className="space-y-5 text-gray-400 leading-relaxed text-lg">
              <p>
                He became fascinated by figures like <span className="text-white font-medium">Zhuge Liang</span> — not because of legend,
                but because of the way they thought.
              </p>
              <p>
                They didn't rely on force.
                <br />
                They didn't depend on luck.
              </p>
              <p>
                They understood <span className="text-amber-200">timing</span>, <span className="text-amber-200">human behaviour</span>, and how to structure outcomes before action was taken.
              </p>
              <p>
                Steven did not try to become Zhuge Liang.
              </p>
              <p>
                Instead, he focused on understanding the <span className="text-white">principles behind his thinking.</span>
              </p>
              <p>
                And more importantly...
              </p>
              <p className="text-white font-medium">
                how to translate those principles into modern life.
              </p>
            </div>
          </div>

          {/* The Pattern */}
          <div className="mb-20" data-testid="about-block-pattern">
            <div className="space-y-5 text-gray-400 leading-relaxed text-lg">
              <p>
                Through years of study, application, and real-world testing, Steven began to see a pattern:
              </p>
              <div className="my-8 space-y-3 pl-4 border-l border-[#252525]">
                <p className="text-gray-300">
                  When timing is right, <span className="text-white">resistance disappears.</span>
                </p>
                <p className="text-gray-300">
                  When the mind is clear, <span className="text-white">decisions become simple.</span>
                </p>
                <p className="text-gray-300">
                  When actions are structured correctly, <span className="text-white">results become predictable.</span>
                </p>
              </div>
              <p>
                That was when everything clicked.
              </p>
            </div>
          </div>

          {/* Three Forces */}
          <div className="mb-20" data-testid="about-block-forces">
            <div className="space-y-5 text-gray-400 leading-relaxed text-lg">
              <p>
                He realised that success is built on three forces:
              </p>
              <div className="my-10 grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-[#111] border border-[#252525] rounded-lg p-6 text-center">
                  <span className="text-4xl md:text-5xl font-serif text-amber-300 block mb-3">生</span>
                  <p className="text-white text-sm uppercase tracking-widest">Timing</p>
                </div>
                <div className="bg-[#111] border border-[#252525] rounded-lg p-6 text-center">
                  <span className="text-4xl md:text-5xl font-serif text-amber-300 block mb-3">我</span>
                  <p className="text-white text-sm uppercase tracking-widest">Clarity</p>
                </div>
                <div className="bg-[#111] border border-[#252525] rounded-lg p-6 text-center">
                  <span className="text-4xl md:text-5xl font-serif text-amber-300 block mb-3">命</span>
                  <p className="text-white text-sm uppercase tracking-widest">Precision</p>
                </div>
              </div>
              <p>
                Because when you have timing <span className="text-amber-200 font-serif">(生)</span>...
                <br />
                your mind becomes clear <span className="text-amber-200 font-serif">(我)</span>...
                <br />
                and your actions become precise <span className="text-amber-200 font-serif">(命)</span>.
              </p>
            </div>
          </div>

          {/* Today */}
          <div className="mb-20" data-testid="about-block-today">
            <div className="space-y-5 text-gray-400 leading-relaxed text-lg">
              <p>
                Today, <span className="text-white font-medium">Steven Yong</span> is known for turning ancient strategic wisdom into practical systems for modern decision-making.
              </p>
              <p>
                Some have described his approach as what a <span className="text-amber-200 font-serif italic">"modern-day Zhuge Liang"</span> might look like —
                not in title, but in thinking.
              </p>
              <p>
                A strategist who does not fight harder...
                <br />
                but aligns <span className="text-white">timing</span>, <span className="text-white">clarity</span>, and <span className="text-white">execution</span> so that outcomes become inevitable.
              </p>
            </div>
          </div>

          {/* Online Tongshu */}
          <div className="mb-20" data-testid="about-block-tongshu">
            <div className="space-y-5 text-gray-400 leading-relaxed text-lg">
              <p>
                Through his work — including his <span className="text-amber-200 font-medium">Online Tongshu</span> —
                he has made what was once reserved for emperors and master strategists accessible for everyday use.
              </p>
            </div>
          </div>

          {/* Closing */}
          <div className="mb-10" data-testid="about-block-closing">
            <div className="border-t border-[#252525] pt-12">
              <p className="text-xl md:text-2xl font-light text-white leading-relaxed text-center">
                Because in the end, success is not about doing more.
              </p>
              <p className="text-xl md:text-2xl font-light text-amber-200 leading-relaxed text-center mt-4 font-serif italic">
                It is about doing the right thing... at the right time... in the right way.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-white mb-6 leading-tight">
            Ready to Start Your
            <br />
            <span className="font-serif italic text-amber-200">Journey?</span>
          </h2>
          <p className="text-gray-400 text-lg mb-10 max-w-xl mx-auto">
            Whether you're new to Chinese Metaphysics or looking to deepen your practice, there's a path for you.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              size="lg"
              onClick={() => navigate('/courses')}
              className="bg-amber-500 hover:bg-amber-400 text-[#0a0a0a] font-medium px-8 py-6 text-base"
              data-testid="about-explore-courses-btn"
            >
              Explore Courses
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/')}
              className="border-[#353535] text-white hover:bg-[#1a1a1a] px-8 py-6 text-base"
              data-testid="about-back-home-btn"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
